import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setIsSubmitted(true);
      toast.success('Check your email for reset instructions');
    } catch (err) {
      // Still show success to prevent email enumeration
      // The backend always returns 200 regardless of email existence
      if (err.response?.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else {
        setIsSubmitted(true);
        toast.success('Check your email for reset instructions');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1737629918399-45526c4f9264?auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/cf-logo.png" alt="CreatorFlow" className="w-12 h-12 rounded-xl" />
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="gradient-text">CreatorFlow</span>
            </h1>
          </div>
        </div>

        <Card className="bg-slate-900/90 border-slate-800 backdrop-blur-xl">
          {!isSubmitted ? (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Mail className="w-5 h-5 text-violet-400" />
                  Reset Password
                </CardTitle>
                <CardDescription>
                  Enter your email address and we'll send you a link to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="forgot-password-email"
                      type="email"
                      placeholder="creator@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-slate-950 border-slate-700"
                      required
                      autoFocus
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-gradient"
                    data-testid="forgot-password-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  Check Your Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300">
                  If an account with <span className="text-white font-medium">{email}</span> exists, 
                  you will receive a password reset link shortly.
                </p>
                <p className="text-slate-400 text-sm">
                  The link will expire in 1 hour. Check your spam folder if you don't see the email.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full border-slate-700 hover:bg-slate-800"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                  data-testid="try-another-email"
                >
                  Try another email
                </Button>
              </CardContent>
            </>
          )}
        </Card>

        <div className="text-center mt-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
